import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiBell, FiMail, FiUser, FiLogOut, FiMenu } from "react-icons/fi";
import { authFetch, logout } from "../api";

function DropdownPanel({ children }) {
  return (
    <div className="absolute right-0 top-full mt-2 w-72 bg-navy-dark border border-navy-border rounded-lg shadow-xl z-50 overflow-hidden">
      {children}
    </div>
  );
}

function CountDot({ n }) {
  if (!n) return null;
  return <span className="absolute top-1 right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-amber-400 text-navy-dark text-[10px] font-bold flex items-center justify-center tabular-nums pointer-events-none" aria-hidden="true">{n > 99 ? "99+" : n}</span>;
}

// `notifications`: what is waiting for a person, [{ label, count, to }]; `unreadMessages`: the number of unread Contact page messages.
export default function AdminTopBar({ onMenu, notifications = [], unreadMessages = 0 }) {
  const [openMenu, setOpenMenu] = useState(null); // 'notifications' | 'messages' | 'profile' | null
  const [profile, setProfile] = useState(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    authFetch("/core/profile/")
      .then((res) => res.json())
      .then(setProfile)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggle = (menu) => setOpenMenu((current) => (current === menu ? null : menu));

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const initial = (profile?.email || profile?.username || "?").charAt(0).toUpperCase();

  return (
    <header className="h-16 shrink-0 flex items-center justify-between lg:justify-end gap-2 px-4 md:px-6 bg-navy border-b border-navy-border sticky top-0 z-30">
      <button type="button" onClick={onMenu} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-gray-300 hover:bg-white/5 hover:text-white" aria-label="Open the menu"><FiMenu size={20} /></button>
      <div ref={containerRef} className="flex items-center gap-2">
        <div className="relative">
          <button
            onClick={() => toggle("notifications")}
            className="relative w-10 h-10 flex items-center justify-center rounded-lg text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
            aria-label={`Notifications${notifications.length ? `: ${notifications.reduce((t, n) => t + n.count, 0)} waiting` : ""}`}
          >
            <FiBell size={18} />
            <CountDot n={notifications.reduce((t, n) => t + n.count, 0)} />
          </button>
          {openMenu === "notifications" && (
            <DropdownPanel>
              <div className="px-4 py-3 border-b border-navy-border font-semibold text-sm">Notifications</div>
              {!notifications.length ? <div className="px-4 py-6 text-sm text-gray-400 text-center">Nothing is waiting for you</div> : notifications.map((n) => (
                <button key={n.label} onClick={() => { setOpenMenu(null); navigate(n.to); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white text-left transition-colors">
                  <span className="flex-1">{n.label}</span><span className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full bg-black/30">{n.count}</span>
                </button>
              ))}
            </DropdownPanel>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => toggle("messages")}
            className="relative w-10 h-10 flex items-center justify-center rounded-lg text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
            aria-label={`Messages${unreadMessages ? `: ${unreadMessages} unread` : ""}`}
          >
            <FiMail size={18} />
            <CountDot n={unreadMessages} />
          </button>
          {openMenu === "messages" && (
            <DropdownPanel>
              <div className="px-4 py-3 border-b border-navy-border font-semibold text-sm">Messages</div>
              {unreadMessages ? (
                <button onClick={() => { setOpenMenu(null); navigate("/admin/pages/contact/messages"); }} className="w-full px-4 py-4 text-sm text-gray-200 hover:bg-white/5 text-left transition-colors">
                  <b>{unreadMessages}</b> unread {unreadMessages === 1 ? "message" : "messages"} from the Contact page <span className="text-accent-light">- open</span>
                </button>
              ) : <div className="px-4 py-6 text-sm text-gray-400 text-center">No unread messages</div>}
            </DropdownPanel>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => toggle("profile")}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg text-gray-200 hover:bg-white/5 transition-colors"
          >
            <span className="w-8 h-8 rounded-full bg-brand-gradient flex items-center justify-center text-sm font-semibold text-white">
              {initial}
            </span>
            <span className="text-sm font-medium max-w-[140px] truncate">
              {profile?.email || "Account"}
            </span>
          </button>
          {openMenu === "profile" && (
            <DropdownPanel>
              <div className="px-4 py-3 border-b border-navy-border">
                <p className="text-sm font-semibold truncate">{profile?.email}</p>
                <p className="text-xs text-gray-400">Signed in</p>
              </div>
              <button
                onClick={() => { setOpenMenu(null); navigate("/admin/profile"); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
              >
                <FiUser size={16} /> Profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-red-500/10 hover:text-red-400 transition-colors"
              >
                <FiLogOut size={16} /> Logout
              </button>
            </DropdownPanel>
          )}
        </div>
      </div>
    </header>
  );
}
